import { useState, useEffect } from "react";
import { styled } from "@glitz/react";
import { fontSize, spacing, Theme, red } from "../theme";
import Input from "./basic/TextInput";
import Button from "./basic/Button";
import {
  isDeliverySelectionComplete,
  useShipping,
} from "../context/ShippingContext";

interface ShippingOptionsProps {
  onClick: () => void;
  /** Visa TMA-leveranswidget under adressfälten */
  children?: React.ReactNode;
  /** Kräv att TMA-leveransval är komplett innan nästa steg */
  requireDeliverySelection?: boolean;
}

type Field =
  | "firstName"
  | "lastName"
  | "address"
  | "zipCode"
  | "city"
  | "phone"
  | "email";

type Errors = Partial<Record<Field, string>>;

const isValidEmail = (value: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

const normalizeSwedishZip = (value: string) => value.replace(/\s+/g, "").trim();

const isValidSwedishZip = (value: string) => {
  return /^\d{5}$/.test(normalizeSwedishZip(value));
};

const normalizePhone = (value: string) => value.replace(/[^\d+]/g, "").trim();

const isValidPhone = (value: string) => {
  const v = normalizePhone(value);
  if (!v) return false;
  if (!/^\+?\d+$/.test(v)) return false;
  const digits = v.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
};

const ShippingOptions = ({
  onClick,
  children,
  requireDeliverySelection = false,
}: ShippingOptionsProps) => {
  const { shipping, setShipping, deliverySelection, updateShippingField } =
    useShipping();

  const [firstName, setFirstName] = useState<string>(shipping.first_name);
  const [lastName, setLastName] = useState<string>(shipping.last_name);
  const [address, setAddress] = useState<string>(shipping.address_1);
  const [zipCode, setZipCode] = useState<string>(shipping.postcode);
  const [city, setCity] = useState<string>(shipping.city);
  const [phone, setPhone] = useState<string>(shipping.phone);
  const [email, setEmail] = useState<string>(shipping.email);

  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string>("");

  useEffect(() => {
    setFirstName(shipping.first_name);
    setLastName(shipping.last_name);
    setAddress(shipping.address_1);
    setZipCode(shipping.postcode);
    setCity(shipping.city);
    setPhone(shipping.phone);
    setEmail(shipping.email);
  }, [
    shipping.first_name,
    shipping.last_name,
    shipping.address_1,
    shipping.postcode,
    shipping.city,
    shipping.phone,
    shipping.email,
  ]);

  const clearFieldError = (field: Field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (formError) setFormError("");
  };

  const validate = (): boolean => {
    const nextErrors: Errors = {};

    if (!firstName.trim()) nextErrors.firstName = "Ange förnamn";
    if (!lastName.trim()) nextErrors.lastName = "Ange efternamn";
    if (!address.trim()) nextErrors.address = "Ange adress";
    if (!city.trim()) nextErrors.city = "Ange stad";

    if (!zipCode.trim()) {
      nextErrors.zipCode = "Ange postnummer";
    } else if (!isValidSwedishZip(zipCode)) {
      nextErrors.zipCode = "Ange ett giltigt postnummer (t.ex. 123 45)";
    }

    if (!email.trim()) {
      nextErrors.email = "Ange e-post";
    } else if (!isValidEmail(email)) {
      nextErrors.email = "Ange en giltig e-postadress";
    }

    if (!phone.trim()) {
      nextErrors.phone = "Ange telefonnummer";
    } else if (!isValidPhone(phone)) {
      nextErrors.phone = "Ange ett giltigt telefonnummer";
    }

    setErrors(nextErrors);

    const isValid = Object.keys(nextErrors).length === 0;
    setFormError(isValid ? "" : "Kontrollera fälten markerade i formuläret.");
    return isValid;
  };

  const onClickNextStep = () => {
    if (!validate()) return;

    // spara uppgifterna i globalt state (shipping)
    setShipping({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      address_1: address.trim(),
      address_2: "",
      postcode: normalizeSwedishZip(zipCode), // sparas som "12345"
      city: city.trim(),
      state: "",
      phone: normalizePhone(phone),
      email: email.trim(),
    });

    if (
      requireDeliverySelection &&
      !isDeliverySelectionComplete(deliverySelection)
    ) {
      setFormError(
        "Välj leveranssätt" +
          (deliverySelection?.requiresServicePoint
            ? " och utlämningsställe"
            : "") +
          " innan du går vidare."
      );
      return;
    }

    onClick();
  };

  return (
    <Wrapper className="shipping-options-wrapper">
      <Header>Leveransadress</Header>
      <FormWrapper onSubmit={(e) => e.preventDefault()}>
        <Input
          label="Förnamn"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            clearFieldError("firstName");
          }}
          error={Boolean(errors.firstName)}
          helpText={errors.firstName}
        />

        <Input
          label="Efternamn"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            clearFieldError("lastName");
          }}
          error={Boolean(errors.lastName)}
          helpText={errors.lastName}
        />

        <Input
          label="Adress"
          placeholder="Gatuadress och Husnummer"
          value={address}
          onChange={(e) => {
            setAddress(e.target.value);
            clearFieldError("address");
          }}
          error={Boolean(errors.address)}
          helpText={errors.address}
        />

        <Input
          label="Postnummer"
          value={zipCode}
          onChange={(e) => {
            const value = e.target.value;
            setZipCode(value);
            clearFieldError("zipCode");
            // Synka till context så TMA-widgeten kan hämta alternativ
            const normalized = normalizeSwedishZip(value);
            if (/^\d{5}$/.test(normalized)) {
              updateShippingField("postcode", normalized);
            }
          }}
          error={Boolean(errors.zipCode)}
          helpText={errors.zipCode}
        />

        <Input
          label="Stad"
          value={city}
          onChange={(e) => {
            const value = e.target.value;
            setCity(value);
            clearFieldError("city");
            if (value.trim()) updateShippingField("city", value.trim());
          }}
          error={Boolean(errors.city)}
          helpText={errors.city}
        />

        <Input
          label="Telefonnummer"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            clearFieldError("phone");
          }}
          error={Boolean(errors.phone)}
          helpText={errors.phone}
        />

        <Input
          label="E-post"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            clearFieldError("email");
          }}
          error={Boolean(errors.email)}
          helpText={errors.email}
        />

        {formError ? <FormErrorText>{formError}</FormErrorText> : null}
      </FormWrapper>
      {children}
      <Button as={Theme.Dark} onClick={onClickNextStep}>
        Nästa steg
      </Button>
    </Wrapper>
  );
};

const Wrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "50%",
  maxWidth: "100%",
  justifySelf: "center",
  gap: spacing.m,
  marginBottom: spacing.xxl,
  boxSizing: "border-box",
});

const FormWrapper = styled.form({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: spacing.m,
  width: "100%",
});

const FormErrorText = styled.div({
  fontSize: fontSize.s,
  color: red,
  fontWeight: 500,
});

const Header = styled.h1({
  textAlign: "center",
});

export default ShippingOptions;
